import { ChevronDown, ChevronRight, Database, Search } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { ColumnChip, type ColumnRole } from '../atoms/ColumnChip'
import { Icon } from '../atoms/Icon'
import { Input } from '../atoms/Input'

/**
 * MVColumnTree — collapsible tree of MVs and their columns, used as the
 * left pane of the Phase-2 Report Builder.
 *
 * Headless w.r.t. drag-and-drop: each rendered ColumnChip can be wrapped
 * in `useDraggable` by the parent via the `renderColumn` render prop. The
 * default rendering shows a static (visual-only) chip useful for
 * Storybook + read-only preview tabs.
 *
 * Search filters by both MV name AND column name (case-insensitive
 * substring). When matched, the parent MV stays expanded.
 *
 * Performance: linear scan over up to ~100 MVs × ~10 columns each = 1000
 * leaves max in P2. Virtualization is deferred to P2.5 if measured.
 */

export interface MVColumnTreeColumn {
  columnName: string
  dataType: string
  columnRole: ColumnRole
  /** Free-form PII class (e.g. 'high', 'restricted'). When 'restricted', */
  /** the chip renders disabled. */
  piiClass?: string
}

export interface MVColumnTreeMV {
  mvName: string
  displayName: string
  category?: string
  description?: string
  columns: MVColumnTreeColumn[]
}

export interface MVColumnTreeProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The MV → columns inventory to render. */
  data: MVColumnTreeMV[]
  /** Initially-expanded MV names. Default: none. */
  defaultExpanded?: string[]
  /** Persistent expanded set; controlled. Pair with onExpandedChange. */
  expanded?: string[]
  /** Notified whenever expansion state changes. */
  onExpandedChange?: (names: string[]) => void
  /** Optional callback fired when a column leaf is clicked. */
  onColumnClick?: (mvName: string, column: MVColumnTreeColumn) => void
  /** Customize a column leaf — wrap with dnd, add a custom action, etc. */
  renderColumn?: (mvName: string, column: MVColumnTreeColumn) => React.ReactNode
  /**
   * Optional render slot rendered next to each MV header label. Use it to
   * inject contextual badges (e.g. "joins via 2 cols", "currently selected",
   * etc). The function receives the MV's full record so it can derive any
   * hint from the same data the tree already has.
   */
  renderMVMeta?: (mv: MVColumnTreeMV) => React.ReactNode
  /**
   * Optional render slot for an MV-level *action* button (e.g. "+ Add all
   * columns"). Rendered between the MV label and the meta badge so it is
   * always reachable without opening the MV. The action callback should
   * stop event propagation to avoid toggling the expand/collapse.
   */
  renderMVAction?: (mv: MVColumnTreeMV) => React.ReactNode
  /** Show the search box at the top. Default true. */
  showSearch?: boolean
  /** Empty-state message when data is []. */
  emptyMessage?: string
}

export const MVColumnTree = React.forwardRef<HTMLDivElement, MVColumnTreeProps>(
  (
    {
      className,
      data,
      defaultExpanded = [],
      expanded,
      onExpandedChange,
      onColumnClick,
      renderColumn,
      renderMVMeta,
      renderMVAction,
      showSearch = true,
      emptyMessage = 'No materialized views available.',
      ...props
    },
    ref,
  ) => {
    const [internalExpanded, setInternalExpanded] = React.useState<string[]>(defaultExpanded)
    const [search, setSearch] = React.useState('')

    const isControlled = expanded !== undefined
    const expandedSet = React.useMemo(
      () => new Set(isControlled ? expanded : internalExpanded),
      [isControlled, expanded, internalExpanded],
    )

    const toggle = (mvName: string) => {
      const next = new Set(expandedSet)
      if (next.has(mvName)) next.delete(mvName)
      else next.add(mvName)
      const arr = Array.from(next)
      if (!isControlled) setInternalExpanded(arr)
      onExpandedChange?.(arr)
    }

    // Filter logic. When search is non-empty:
    //   - Match an MV if its display_name OR mv_name contains the term.
    //   - Match a column if its column_name contains the term.
    //   - When ANY column matches, force-expand the parent MV.
    const filtered = React.useMemo(() => {
      // Defensive: coerce to string in case the controlled value is
      // mistakenly fed a non-string (e.g. a synthetic ChangeEvent if the
      // consumer wired its onChange wrong). Avoids hard-crashing the
      // tree and surfaces an empty filter instead.
      const safe = typeof search === 'string' ? search : ''
      if (!safe.trim()) return data
      const q = safe.toLowerCase()
      const matchedMVNames: string[] = []
      const out = data
        .map((mv) => {
          const mvMatches =
            mv.mvName.toLowerCase().includes(q) || mv.displayName.toLowerCase().includes(q)
          const matchedCols = mv.columns.filter((c) => c.columnName.toLowerCase().includes(q))
          if (mvMatches) {
            matchedMVNames.push(mv.mvName)
            return mv // keep ALL columns for an MV name match
          }
          if (matchedCols.length > 0) {
            matchedMVNames.push(mv.mvName)
            return { ...mv, columns: matchedCols }
          }
          return null
        })
        .filter((mv): mv is MVColumnTreeMV => mv !== null)
      // Side-effect-free: caller can opt to auto-expand by listening on
      // onExpandedChange with the merged set if desired.
      return out
    }, [data, search])

    // When searching, automatically display MVs as expanded (visual only —
    // doesn't mutate the controlled state).
    const isSearching = typeof search === 'string' && !!search.trim()

    if (data.length === 0) {
      return (
        <div
          ref={ref}
          className={cn('text-caption text-ia-text-tertiary p-4', className)}
          {...props}
        >
          {emptyMessage}
        </div>
      )
    }

    return (
      <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
        {showSearch && (
          <div className="px-2">
            <Input
              type="search"
              placeholder="Search MVs / columns…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={Search}
            />
          </div>
        )}
        <div className="flex flex-col gap-1 overflow-auto">
          {filtered.length === 0 ? (
            <div className="text-caption text-ia-text-tertiary p-4 text-center">
              No matches for "{search}"
            </div>
          ) : (
            filtered.map((mv) => {
              const open = isSearching || expandedSet.has(mv.mvName)
              return (
                <div key={mv.mvName} className="">
                  <button
                    type="button"
                    onClick={() => toggle(mv.mvName)}
                    aria-expanded={open}
                    className="flex w-full items-center gap-1 rounded-md px-2 py-1.5 text-body-sm text-ia-text hover:bg-ia-elevated"
                  >
                    {open ? (
                      <ChevronDown className="h-3.5 w-3.5 text-ia-text-tertiary shrink-0" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-ia-text-tertiary shrink-0" />
                    )}
                    <Icon icon={Database} size="xs" className="text-ia-text-tertiary shrink-0" />
                    <span className="font-medium truncate">{mv.displayName}</span>
                    <span className="ml-auto inline-flex items-center gap-1.5 shrink-0">
                      {renderMVAction && (
                        <span onClick={(e) => e.stopPropagation()}>{renderMVAction(mv)}</span>
                      )}
                      {renderMVMeta ? (
                        renderMVMeta(mv)
                      ) : (
                        <span className="text-caption text-ia-text-tertiary">
                          {mv.columns.length}
                        </span>
                      )}
                    </span>
                  </button>
                  {open && (
                    <div className="ml-5 mt-1 mb-2 flex flex-col gap-1">
                      {mv.columns.map((col) => {
                        if (renderColumn) {
                          return (
                            <React.Fragment key={`${mv.mvName}:${col.columnName}`}>
                              {renderColumn(mv.mvName, col)}
                            </React.Fragment>
                          )
                        }
                        const restricted = col.piiClass === 'restricted'
                        return (
                          <ColumnChip
                            key={`${mv.mvName}:${col.columnName}`}
                            label={col.columnName}
                            columnRole={col.columnRole}
                            disabled={restricted}
                            draggable={false}
                            onClick={() => onColumnClick?.(mv.mvName, col)}
                          />
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    )
  },
)
MVColumnTree.displayName = 'MVColumnTree'
